// src/screens/BottomTabsLayout.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { FiLayers, FiActivity, FiUser,FiTrendingUp,FiClock } from "react-icons/fi";

import { apiFetch } from "../api";
import { useSettings } from "../settings";
import { useAuth } from "../auth/AuthProvider";

export default function BottomTabsLayout() {
  const location = useLocation();
  const { settings } = useSettings();
  const { auth } = useAuth();

  // -----------------------------
  // ✅ badge da "Carteira" (operações pendentes)
  // -----------------------------
  const [walletBadge, setWalletBadge] = useState({ level: "NONE", count: 0 });

  // -----------------------------
  // ids / prefs mínimas
  // -----------------------------
  const accountId = settings?.accountId ?? auth?.accountId ?? null;

  // ✅ topK pode ser UI preference (opcional mandar; backend já tem default por usuário)
  const topK = settings?.planTopK ?? 5;

  // -----------------------------
  // QS builders (mínimos)
  // -----------------------------
  const buildActionsQS = useCallback(() => {
    const qs = new URLSearchParams();
    if (accountId != null) qs.set("accountId", String(accountId));

    // opcional: mandar topK (se não quiser mandar, remova essas 2 linhas)
    if (topK != null) qs.set("topK", String(topK));

    return qs;
  }, [accountId, topK]);

  // monitoringv2 não precisa de QS
  const monitoringV2Url = useMemo(() => `/api/portfolio/monitoringv2`, []);

  // -----------------------------
  // cache simples em memória
  // -----------------------------
  const cacheRef = useRef(new Map());
  const CACHE_TTL_MS = 60_000;

  const cachedFetch = useCallback(async (url, ttlMs = CACHE_TTL_MS) => {
    const now = Date.now();
    const hit = cacheRef.current.get(url);
    if (hit && now - hit.ts < ttlMs) return hit.data;

    const data = await apiFetch(url);
    cacheRef.current.set(url, { ts: now, data });
    return data;
  }, []);

  // -----------------------------
  // Prefetch leve (monitoringv2 + actions)
  // -----------------------------
  const prefetchAll = useCallback(() => {
    // monitoringv2 (não depende de accountId)
    cachedFetch(monitoringV2Url).catch(() => {});

    // actions (depende de accountId)
    if (accountId == null) return;
    const actUrl = `/api/portfolio/actions?${buildActionsQS().toString()}`;
    cachedFetch(actUrl).catch(() => {});
  }, [cachedFetch, monitoringV2Url, accountId, buildActionsQS]);

  // -----------------------------
  // ✅ Badge Carteira
  // -----------------------------
  const refreshWalletBadge = useCallback(async () => {
    // fonte de verdade: /api/portfolio/actions
    if (accountId == null) {
      setWalletBadge({ level: "NONE", count: 0 });
      return;
    }

    try {
      const urlA = `/api/portfolio/actions?${buildActionsQS().toString()}`;
      const actions = await cachedFetch(urlA, CACHE_TTL_MS);

      const buyNow = Array.isArray(actions?.actions?.buy) ? actions.actions.buy : [];
      const sellNow = Array.isArray(actions?.actions?.sell) ? actions.actions.sell : [];

      const needSet = new Set();
      for (const it of buyNow) if (it?.symbol) needSet.add(String(it.symbol).toUpperCase());
      for (const it of sellNow) if (it?.symbol) needSet.add(String(it.symbol).toUpperCase());

      const count = needSet.size;
      setWalletBadge({ level: count > 0 ? "SIGNAL_IMMINENT" : "NONE", count });
      return;
    } catch {
      // fallback: não quebra UX
    }

    // fallback: tenta monitoringv2 (não é “fonte de verdade”, só para não mostrar badge errado)
    try {
      await cachedFetch(monitoringV2Url, CACHE_TTL_MS);
      setWalletBadge({ level: "NONE", count: 0 });
    } catch {
      setWalletBadge({ level: "NONE", count: 0 });
    }
  }, [accountId, buildActionsQS, cachedFetch, monitoringV2Url]);

  // Timer global
  useEffect(() => {
    prefetchAll();
    refreshWalletBadge();

    const id = setInterval(() => {
      prefetchAll();
      refreshWalletBadge();
    }, 60_000);

    return () => clearInterval(id);
  }, [prefetchAll, refreshWalletBadge]);

  // -----------------------------
  // active tab
  // -----------------------------
  const activeTab = useMemo(() => {
    if (location.pathname.startsWith("/home")) return "home";
    if (location.pathname.startsWith("/today")) return "today";
    if (location.pathname.startsWith("/buyandsell")) return "buyandsell";
    if (location.pathname.startsWith("/monitoring")) return "monitoring";
    if (location.pathname.startsWith("/config")) return "config";
    return "today";
  }, [location.pathname]);

  return (
    <div style={styles.shell}>
      <div style={styles.topScrim} />

      <div style={styles.content}>
        <Outlet />
      </div>

      <nav style={styles.tabbar}>
        <Tab
          to="/home"
          label="Hoje"
          icon={<FiClock size={20} />}
          active={activeTab === "home"}
        />

        <Tab
          to="/today"
          label="Carteira"
          icon={<FiLayers size={20} />}
          active={activeTab === "today"}
        />

        <Tab
          to="/buyandsell"
          label="Ações"
          icon={<FiActivity size={20} />}
          active={activeTab === "buyandsell"}
          badgeLevel={walletBadge.level}
          badgeCount={walletBadge.count}
        />

        <Tab
          to="/monitoring"
          label="Ativos"
          icon={<FiTrendingUp size={20} />}
          active={activeTab === "monitoring"}
        />

        <Tab to="/config" label="Conta" icon={<FiUser size={20} />} active={activeTab === "config"} />
      </nav>
    </div>
  );
}

function Tab({ to, label, icon, active, badgeLevel, badgeCount }) {
  const show = badgeLevel && badgeLevel !== "NONE" && Number(badgeCount || 0) > 0;

  return (
    <NavLink
      to={to}
      style={{
        ...styles.tab,
        ...(active ? styles.tabActive : null),
      }}
    >
      <div style={styles.iconWrap}>
        {icon}

        {show ? (
          <span
            title={`${badgeLevel} • ${badgeCount} itens`}
            style={{
              ...styles.badge,
              ...(badgeCount >= 10 ? styles.badgeBig : null),
              ...(badgeLevel === "WATCH" ? styles.badgeWatch : null),
              ...(badgeLevel === "SETUP_FORMING" ? styles.badgeSetup : null),
              ...(badgeLevel === "SIGNAL_IMMINENT" ? styles.badgeImminent : null),
            }}
          >
            {badgeCount >= 10 ? "9+" : String(badgeCount)}
          </span>
        ) : null}
      </div>

      <div style={styles.label}>{label}</div>
      <div style={{ ...styles.underline, ...(active ? styles.underlineOn : null) }} />
    </NavLink>
  );
}

const TAB_H = 64;

const styles = {
  shell: {
    position: "fixed",
    inset: 0,
    display: "flex",
    flexDirection: "column",
    background: "#0a0a0c",
    paddingTop: "max(env(safe-area-inset-top), 24px)",
  },

  topScrim: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    height: "max(env(safe-area-inset-top), 24px)",
    background: "rgba(10,10,12,0.90)",
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
    zIndex: 80,
    pointerEvents: "none",
  },

  content: {
    flex: "1 1 auto",
    minHeight: 0,
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    overscrollBehaviorY: "contain",
    paddingBottom: `calc(${TAB_H}px + 12px + env(safe-area-inset-bottom))`,
    color: "var(--text-primary)",
  },

  tabbar: {
    height: `${TAB_H}px`,
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    alignItems: "center",
    borderTop: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(10,10,12,0.92)",
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
    zIndex: 70,
    paddingBottom: "env(safe-area-inset-bottom)",
  },

  tab: {
    height: "100%",
    color: "rgba(255,255,255,0.70)",
    textDecoration: "none",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
    position: "relative",
  },
  tabActive: { color: "rgba(255,255,255,0.98)" },

  iconWrap: { position: "relative", lineHeight: 0 },
  label: { fontSize: 12 },

  underline: {
    position: "absolute",
    bottom: 6,
    width: 26,
    height: 3,
    borderRadius: 999,
    opacity: 0,
    background: "rgba(255,255,255,0.90)",
    transform: "scaleX(0.6)",
    transition: "opacity 150ms ease, transform 150ms ease",
  },
  underlineOn: { opacity: 1, transform: "scaleX(1)" },

  // ✅ Badge styles
  badge: {
    position: "absolute",
    top: -6,
    right: -8,
    minWidth: 10,
    height: 10,
    padding: "0 2px",
    borderRadius: 999,
    border: "1px solid rgba(0,0,0,0.35)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 9,
    fontWeight: 800,
    color: "rgba(0,0,0,0.85)",
    background: "rgba(255,70,70,0.95)", // default (imminent)
  },
  badgeBig: { minWidth: 18, height: 18, top: -10, right: -12, fontSize: 10 },

  // níveis (se um dia você quiser diferenciar)
  badgeWatch: { background: "rgba(255,200,80,0.95)" },
  badgeSetup: { background: "rgba(255,140,70,0.95)" },
  badgeImminent: { background: "rgba(255,70,70,0.95)" },
};
